/**
 * Ce qu'une fiche client et un devis doivent contenir avant de servir.
 *
 * Fonctions PURES : les règles se testent sans écran ni réseau.
 *
 *  - Un client (ou un prospect) sans mobile ne reçoit ni relance ni rappel par
 *    SMS : Octopush refuse un numéro fixe ou mal écrit.
 *  - Un devis dont une ligne a une TVA à 0 % (ou pas de TVA du tout) est faux :
 *    il ne part pas chez le client, et ne devient pas une facture.
 */

/** Le mobile au format international (+33612345678), ou `null` s'il n'en est pas un. */
export function mobileInternational(saisie: string): string | null {
  let s = saisie.replace(/[\s.\-()]/g, '');
  if (s.startsWith('00')) s = '+' + s.slice(2);
  if (/^0[67]\d{8}$/.test(s)) return '+33' + s.slice(1);
  if (s.startsWith('+33')) return /^\+33[67]\d{8}$/.test(s) ? s : null;
  // Un numéro étranger : on ne connaît pas ses règles, on vérifie seulement sa forme.
  if (/^\+[1-9]\d{7,14}$/.test(s)) return s;
  return null;
}

/** Pourquoi ce mobile ne convient pas, ou `null` s'il convient. */
export function erreurMobile(saisie: string): string | null {
  const s = saisie.replace(/[\s.\-()]/g, '');
  if (s === '') return 'Mobile obligatoire (relances et rappels par SMS)';
  if (mobileInternational(s) !== null) return null;
  if (/^(0|\+33|0033)[1-59]/.test(s)) {
    return 'Ce numéro est un fixe : il faut un portable (06 ou 07)';
  }
  return 'Numéro de portable incomplet ou mal écrit (ex. 06 12 34 56 78)';
}

/**
 * Le portable d'une fiche : le champ « Mobile » s'il est rempli, sinon le
 * champ « Téléphone » quand c'est un portable (beaucoup de fiches n'ont que
 * celui-là). `null` si la fiche n'a aucun portable utilisable.
 */
export function portableDuContact(mobile: string, telephone: string): string | null {
  if (mobile.trim() !== '') return mobileInternational(mobile) === null ? null : mobile.trim();
  return mobileInternational(telephone) === null ? null : telephone.trim();
}

/** Pourquoi la fiche n'a pas de portable utilisable, ou `null` si elle en a un. */
export function erreurPortable(mobile: string, telephone: string): string | null {
  if (mobile.trim() !== '') return erreurMobile(mobile);
  if (portableDuContact('', telephone) !== null) return null;
  if (telephone.trim() !== '') {
    return 'Le téléphone saisi n’est pas un portable : ajoutez un portable (06 ou 07)';
  }
  return erreurMobile('');
}

export interface LigneATaxer {
  /** Le total HT de la ligne. */
  totalHt: number;
  /** Le taux de TVA (0.2 pour 20 %), ou `null` si la ligne n'en a pas. */
  taux: number | null;
}

/**
 * Les numéros (à partir de 1) des lignes sans TVA. Une ligne à 0 € ne compte
 * pas : un commentaire ou une option offerte ne fausse aucun montant.
 */
export function lignesSansTva(lignes: readonly LigneATaxer[]): number[] {
  const fautives: number[] = [];
  lignes.forEach((l, i) => {
    if (Math.abs(l.totalHt) >= 0.005 && (l.taux === null || l.taux <= 0)) fautives.push(i + 1);
  });
  return fautives;
}

/** La phrase qui dit quelles lignes corriger. */
export function messageLignesSansTva(numeros: readonly number[]): string {
  const liste =
    numeros.length === 1
      ? `La ligne ${String(numeros[0])} n'a`
      : `Les lignes ${numeros.slice(0, -1).join(', ')} et ${String(numeros[numeros.length - 1])} n'ont`;
  return `${liste} pas de taux de TVA (ou un taux à 0 %). Le devis ne peut être ni envoyé, ni accepté, ni converti en facture tant que ce n'est pas corrigé.`;
}
